// Кнопка действия (components/Button/README.md): главная, вторичная, тихая, опасная, мягкая;
// красная текстовая — опасное действие вне окна подтверждения («Опасная зона» настроек).
import type { ButtonHTMLAttributes, MouseEvent, Ref } from 'react';
import { cx } from '../lib/cx.ts';
import { Icon } from '../lib/icon.tsx';
import type { LucideIcon } from '../lib/icon.tsx';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'danger-text' | 'soft';
export type ButtonSize = 'md' | 'sm';

const variantClass: Record<ButtonVariant, string> = {
  primary: 't-btn-primary',
  secondary: 't-btn-secondary',
  ghost: 't-btn-ghost',
  danger: 't-btn-danger',
  'danger-text': 't-btn-danger-text',
  soft: 't-btn-soft',
};

/** Классы кнопки — для ссылки, которая выглядит как кнопка. */
export function buttonClass(
  options: { variant?: ButtonVariant; size?: ButtonSize; loading?: boolean } = {},
): string {
  const { variant = 'secondary', size = 'md', loading = false } = options;
  return cx('t-btn', variantClass[variant], size === 'sm' && 't-btn-sm', loading && 'is-loading');
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * primary — одно главное действие, последнее справа; danger — только в окне подтверждения;
   * danger-text — опасное действие в «Опасной зоне» настроек (ux.md, раздел 11).
   */
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Иконка слева от подписи. */
  icon?: LucideIcon;
  /** Спиннер на месте текста той же ширины; повторное нажатие не срабатывает. */
  loading?: boolean;
  ref?: Ref<HTMLButtonElement>;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  icon,
  loading = false,
  className,
  type = 'button',
  onClick,
  children,
  ...rest
}: ButtonProps) {
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (loading) {
      event.preventDefault();
      return;
    }
    onClick?.(event);
  };
  return (
    <button
      type={type}
      className={cx(buttonClass({ variant, size, loading }), className)}
      aria-busy={loading || undefined}
      onClick={handleClick}
      {...rest}
    >
      {icon ? <Icon icon={icon} /> : null}
      {children}
    </button>
  );
}
